import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Link as LinkIcon, X, CheckCircle2, Download } from "lucide-react";

export default function ToconlineImport({ onImported }) {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(false);
  const [openConnect, setOpenConnect] = useState(false);
  const [code, setCode] = useState("");
  const [exchanging, setExchanging] = useState(false);
  const [syncing, setSyncing] = useState(false);

  async function refreshStatus() {
    setLoading(true);
    try {
      const { data } = await api.get("/toconline/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshStatus();
  }, []);

  async function openAuthorize() {
    try {
      const { data } = await api.get("/toconline/authorize_url");
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.info("Autorize no TOConline e copie o código de retorno");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function submitCode() {
    if (!code.trim()) return toast.error("Cole o código antes de continuar");
    setExchanging(true);
    try {
      await api.post("/toconline/exchange", { code: code.trim() });
      toast.success("TOConline ligado com sucesso");
      setOpenConnect(false);
      setCode("");
      await refreshStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExchanging(false);
    }
  }

  async function sync() {
    setSyncing(true);
    try {
      const { data } = await api.post("/toconline/sync-clients");
      toast.success(
        `Importação: ${data.imported} novos, ${data.updated} atualizados, ${data.skipped} ignorados`
      );
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Desligar a integração TOConline?")) return;
    try {
      await api.post("/toconline/disconnect");
      toast.success("Desligado");
      await refreshStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="inline-flex items-center gap-2" data-testid="toconline-integration">
      {status.connected ? (
        <>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-chivo font-bold uppercase tracking-wider bg-[#16A34A]/10 text-[#15803D] border border-[#16A34A]/30 px-2.5 py-1.5 rounded-sm">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> TOConline ligado
          </span>
          <button
            data-testid="toconline-sync-btn"
            onClick={sync}
            disabled={syncing}
            className="bg-neutral-950 text-white hover:bg-neutral-800 rounded-sm px-4 py-2.5 font-manrope font-semibold text-sm flex items-center gap-2 disabled:opacity-60 transition-colors"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <Download className="w-4 h-4" strokeWidth={1.8} />
            )}
            {syncing ? "A importar…" : "Importar do TOConline"}
          </button>
          <button
            data-testid="toconline-disconnect-btn"
            onClick={disconnect}
            className="text-xs font-manrope text-neutral-500 hover:text-[#FF4500] px-2 py-1"
            title="Desligar"
          >
            Desligar
          </button>
        </>
      ) : (
        <button
          data-testid="toconline-connect-btn"
          onClick={() => setOpenConnect(true)}
          disabled={loading}
          className="bg-white border border-zinc-200 hover:border-neutral-950 text-neutral-950 rounded-sm px-4 py-2.5 font-manrope font-semibold text-sm flex items-center gap-2 transition-colors"
        >
          <LinkIcon className="w-4 h-4" strokeWidth={1.8} />
          Ligar TOConline
        </button>
      )}

      {openConnect && (
        <div
          className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpenConnect(false)}
        >
          <div
            className="bg-white rounded-sm w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="toconline-connect-modal"
          >
            <div className="border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
                  Integração
                </div>
                <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
                  Ligar TOConline
                </h2>
              </div>
              <button
                onClick={() => setOpenConnect(false)}
                className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-8 space-y-5">
              <ol className="space-y-3 text-sm font-manrope text-neutral-700 list-decimal list-inside">
                <li>Clique em <strong>Abrir TOConline</strong> e autorize a aplicação.</li>
                <li>
                  Após autorizar, o Postman irá abrir com um URL do tipo
                  <span className="block mt-1 text-xs font-mono text-neutral-500 bg-zinc-50 p-2 rounded-sm">
                    https://oauth.pstmn.io/v1/callback?code=<strong>XXXX</strong>
                  </span>
                </li>
                <li>Copie apenas o valor após <code>code=</code> e cole abaixo.</li>
              </ol>

              <button
                data-testid="toconline-open-auth"
                onClick={openAuthorize}
                className="w-full bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-4 py-2.5 font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
                Abrir TOConline
              </button>

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Código de autorização
                </label>
                <input
                  data-testid="toconline-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Cole aqui o code…"
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-mono text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-200">
                <button
                  onClick={() => setOpenConnect(false)}
                  className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm"
                >
                  Cancelar
                </button>
                <button
                  data-testid="toconline-submit-code"
                  onClick={submitCode}
                  disabled={exchanging || !code.trim()}
                  className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60"
                >
                  {exchanging ? "A ligar…" : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
